import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification, removeNotification } from '~/slices/appSlice';
import type { AppDispatch } from '~/store';

export type ToastType = 'success' | 'error' | 'info' | 'working';

export interface ToastOptions {
  text: string;
  type?: ToastType;
  duration?: number; // Duration in milliseconds, 0 for permanent
}

export const useToast = () => {
  const dispatch = useDispatch<AppDispatch>();

  const showToast = useCallback((options: ToastOptions) => {
    const { text, type = 'info', duration = type === 'working' ? 0 : 5000 } = options;
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    dispatch(addNotification({
      id,
      text,
      type,
    }));

    // Auto-remove toast after specified duration (unless duration is 0 or type is 'working')
    if (duration > 0 && type !== 'working') {
      setTimeout(() => {
        dispatch(removeNotification(id));
      }, duration);
    }

    return id; // Return ID in case manual removal is needed
  }, [dispatch]);

  const hideToast = useCallback((id: string) => {
    dispatch(removeNotification(id));
  }, [dispatch]);

  const showSuccess = useCallback((text: string, duration?: number) => {
    return showToast({ text, type: 'success', duration });
  }, [showToast]);

  const showError = useCallback((text: string, duration?: number) => {
    return showToast({ text, type: 'error', duration });
  }, [showToast]);

  const showInfo = useCallback((text: string, duration?: number) => {
    return showToast({ text, type: 'info', duration });
  }, [showToast]);

  const showWorking = useCallback((text: string) => {
    return showToast({ text, type: 'working', duration: 0 });
  }, [showToast]);

  return {
    showToast,
    hideToast,
    showSuccess,
    showError,
    showInfo,
    showWorking,
  };
};

export default useToast;